import { atom } from 'jotai'
import { atomWithStorage } from 'jotai/utils'

// Persisted speech preferences.
//
// Model: manual playback (the per-segment ▶ buttons) is ALWAYS available — a
// player can play or replay any line whenever they like. The settings here only
// govern AUTO-PLAY ("speak everything"): whether each beat/segment speaks itself
// as it appears.
//
//  - autoPlayDefault: the global default for auto-play across all stories.
//  - perStoryAutoPlay: per-story override, keyed by story id, value 'on' | 'off'
//    | undefined. undefined (or absent) means "use the global default".
export const speechSettingsAtom = atomWithStorage(
  'bw_speechSettings',
  { autoPlayDefault: false, perStoryAutoPlay: {} },
  undefined,
  { getOnInit: true },
)

// The story currently being played, set by GameScreen2 so the auto-play
// resolver and the dock toggle know which override applies. Session-scoped.
export const activeSpeechStoryIdAtom = atom(null)

// Resolved auto-play state for the active story: the per-story override if set,
// otherwise the global default. This is what the player reads to decide whether
// to auto-speak.
export const autoPlayResolvedAtom = atom((get) => {
  const { autoPlayDefault, perStoryAutoPlay } = get(speechSettingsAtom)
  const storyId = get(activeSpeechStoryIdAtom)
  const override = storyId ? perStoryAutoPlay?.[storyId] : undefined
  if (override === 'on') return true
  if (override === 'off') return false
  return !!autoPlayDefault
})

// The id of the segment currently being spoken (e.g. "beat:<id>:0" or
// "chat:<messageKey>"), or null when nothing is playing. Session-scoped so it
// resets on reload. Drives the play/stop affordance on each segment.
export const currentlyPlayingAtom = atom(null)

// --- Lip-sync ---------------------------------------------------------------
//
// The character whose portrait should animate right now: the id of the
// character voicing the currently-playing segment, or null for narrator /
// nothing. Set by useSpeech as the audio queue advances. (The narrator has no
// character id — use activeSpeakingAtom, not this, to know whether a voice is
// playing.)
export const activeSpeakerIdAtom = atom(null)

// Whether ANY voice is currently speaking — narrator included. This is the
// signal the speech visualizer keys off, so the bars animate for the narrator
// (who has no character id) just as they do for a character.
export const activeSpeakingAtom = atom(false)

// Display name of the current voice ("Narrator" for narrator-family segments,
// the character's name otherwise), or null when nothing is playing. Shown under
// the portrait, above the visualizer.
export const activeSpeakerNameAtom = atom(null)

// The Polly viseme value active at the current playback time (one of the ~14
// Polly visemes: p t S T f k i r s u @ a e o, plus 'sil' for silence/closed).
// LipSyncPortrait maps this to a mouth shape. Defaults closed.
export const currentVisemeAtom = atom('sil')
